import React, { useEffect, useState } from 'react';
import heroCoach from '../assets/hero_coach.png';
import { Bell, User, Sun, Moon, Settings, LogOut, Search, Plus, BookOpen, Calendar, HelpCircle, Users, MessageSquare } from 'lucide-react';
import CoachWorkoutLibrary from './CoachWorkoutLibrary';
import CoachBentoDashboard from './CoachBentoDashboard';
import CoachDiscountProgress from './CoachDiscountProgress';
import NousLogo from './NousLogo';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function CoachDashboard({ 
  clients,
  loading,
  onSelectAthlete,
  userProfile,
  unreadCount,
  handleOpenMessaging,
  handleOpenNotifications,
  hasNotifications,
  isOutdoorMode,
  setIsOutdoorMode,
  setShowSettingsModal,
  handleLogout,
  onStartTour
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  
  // States for Add Athlete modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [athleteName, setAthleteName] = useState('');
  const [athleteEmail, setAthleteEmail] = useState('');
  const [addMessage, setAddMessage] = useState({ text: '', type: '' });
  const [isAdding, setIsAdding] = useState(false);
  const [magicLink, setMagicLink] = useState('');
  const [currentView, setCurrentView] = useState('athletes'); // 'athletes' | 'library' | 'periodization'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'alerta'
  const [extraDemoClients, setExtraDemoClients] = useState([]);
  const allClients = [...(clients || []), ...extraDemoClients];

  const loadDemoSquad = () => {
    const demoClients = [
      { id: 'demo-1', name: 'Carlos Demo', email: 'carlos@demo.com', recentStatus: 'Fatiga Alta', readinessScore: 35, status: 'active', lastActivity: 'Hoy, 08:00', tags: ['Ciclista', 'Sub-23'], avatar: 'https://ui-avatars.com/api/?name=Carlos+Demo&background=ef4444&color=fff' },
      { id: 'demo-2', name: 'Ana Demo', email: 'ana@demo.com', recentStatus: 'Recuperación', readinessScore: 65, status: 'active', lastActivity: 'Ayer, 18:30', tags: ['Triatlón', 'Élite'], avatar: 'https://ui-avatars.com/api/?name=Ana+Demo&background=f59e0b&color=fff' },
      { id: 'demo-3', name: 'Luis Demo', email: 'luis@demo.com', recentStatus: 'Óptimo', readinessScore: 92, status: 'active', lastActivity: 'Hoy, 07:15', tags: ['Individual'], avatar: 'https://ui-avatars.com/api/?name=Luis+Demo&background=10b981&color=fff' }
    ];
    setExtraDemoClients(demoClients);
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    if (!athleteEmail || !athleteName) return;
    
    setIsAdding(true);
    setAddMessage({ text: '', type: '' });
    setMagicLink('');
    
    try {
      const res = await fetch(`${API_URL}/coach/invite`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email: athleteEmail, name: athleteName })
      });
      const data = await res.json();
      
      if (res.ok) {
        setAddMessage({ text: 'Invitación generada correctamente.', type: 'success' });
        setMagicLink(data.link);
        setAthleteEmail('');
        setAthleteName('');
      } else {
        setAddMessage({ text: data.error || 'Error al invitar atleta', type: 'error' });
      }
    } catch (error) {
      setAddMessage({ text: 'Error de conexión', type: 'error' });
    } finally {
      setIsAdding(false);
    }
  };


  if (loading) {
    return <div className="glass-panel" style={{ padding: '3rem', textAlign: 'center' }}>Cargando tus atletas...</div>;
  }

  const statusPriority = { 'Fatiga Alta': 1, 'Recuperación': 2, 'Óptimo': 3, 'Sin datos': 4 };
  const statusColor = { 'Fatiga Alta': '#ef4444', 'Recuperación': '#f59e0b', 'Óptimo': 'var(--health-green)', 'Sin datos': 'rgba(255,255,255,0.2)' };
  
  const sortedClients = [...allClients].sort((a, b) => {
     const pA = statusPriority[a.recentStatus] || 4;
     const pB = statusPriority[b.recentStatus] || 4;
     if (pA !== pB) return pA - pB;
     return (a.readinessScore || 0) - (b.readinessScore || 0);
  });
  const filteredClients = sortedClients.filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          client.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (statusFilter === 'alerta') {
      const isAlerta = client.recentStatus === 'Fatiga Alta' || (client.readinessScore && client.readinessScore < 40);
      return matchesSearch && isAlerta;
    }
    return matchesSearch;
  });

  return (
    <div className="dashboard-main" style={{ animation: 'fadeInTab 0.4s ease-out' }}>
      {/* COMPACT TOP BAR */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 className="stagger-1" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', lineHeight: 1.1, fontSize: '2rem', margin: 0 }}>
            <span style={{ fontWeight: 800 }}>AI Command Center</span>
            <span style={{ color: '#8b5cf6', fontSize: '0.75rem', fontWeight: 700, background: 'rgba(139, 92, 246, 0.15)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontFamily: 'monospace' }}>COACH HUB</span>
          </h1>
          <p className="stagger-2" style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', margin: '0.5rem 0 0 0' }}>
            Triage matutino y análisis predictivo de tu plantilla.
          </p>
        </div>

        <div className="stagger-3" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ position: 'relative', width: '250px' }}>
            <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '1rem', color: 'rgba(255,255,255,0.5)', pointerEvents: 'none' }}>
              <Search size={16} />
            </div>
            <input 
              type="text" 
              placeholder="Buscar atleta..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ 
                width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem', borderRadius: '24px', 
                background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', 
                color: 'white', outline: 'none', fontSize: '0.9rem',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
              onBlur={(e) => e.target.style.background = 'rgba(255,255,255,0.05)'}
            />
          </div>

          <button
            onClick={() => setCurrentView('library')}
            style={{
              background: 'rgba(255,255,255,0.1)', border: 'none',
              color: '#fff', padding: '0.6rem 1rem', borderRadius: '24px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold', fontSize: '0.9rem', transition: 'all 0.2s'
            }}
          >
            <BookOpen size={16} /> Plantillas
          </button>

          <button
            onClick={() => setIsAddModalOpen(true)}
            style={{
              background: 'var(--health-cyan)', border: 'none',
              color: '#000', padding: '0.6rem 1.2rem', borderRadius: '24px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold', fontSize: '0.9rem', transition: 'all 0.2s',
              boxShadow: '0 4px 15px rgba(0, 229, 255, 0.2)'
            }}
          >
            <Plus size={16} strokeWidth={3} /> Añadir Atleta
          </button>
        </div>
      </div>

      {/* <div style={{ padding: '0 2rem', marginBottom: '2rem' }}>
        <CoachDiscountProgress athleteCount={clients.length} />
      </div> */}

      {currentView === 'library' ? (
        <CoachWorkoutLibrary onClose={() => setCurrentView('athletes')} clients={allClients} />
      ) : currentView === 'periodization' ? (
        <div className="glass-panel" style={{ padding: '3rem', textAlign: 'center' }}>
          <h2>Gestor de Periodización</h2>
          <p className="text-muted">La funcionalidad de Macrociclos y Microciclos estará disponible próximamente.</p>
          <button onClick={() => setCurrentView('athletes')} className="form-input" style={{ marginTop: '1rem', cursor: 'pointer' }}>Volver al inicio</button>
        </div>
      ) : (
        <>
      {allClients.length === 0 ? (
        <div className="glass-panel" style={{ padding: '4rem 2rem', textAlign: 'center', marginTop: '2rem' }}>
          <h2 className="text-3xl text-gradient">Aún no tienes atletas</h2>
          <p className="text-muted" style={{ marginTop: '1rem', marginBottom: '2rem', fontSize: '1.2rem' }}>
            Añade a tu primer deportista para comenzar a monitorizar su rendimiento y salud.
          </p>
        </div>
      ) : filteredClients.length === 0 && statusFilter === 'all' ? (
        <div className="glass-panel" style={{ padding: '4rem 2rem', textAlign: 'center', marginTop: '2rem' }}>
          <h2 className="text-2xl" style={{ color: 'white' }}>No se encontraron atletas</h2>
          <p className="text-muted" style={{ marginTop: '1rem' }}>No hay resultados para "{searchQuery}".</p>
        </div>
      ) : (
        <>
          <CoachBentoDashboard 
            clients={allClients} 
            onFilterChange={(filter) => {
              // Toggle filter
              setStatusFilter(prev => prev === filter ? 'all' : filter);
            }} 
            onSelectAthlete={onSelectAthlete}
          />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2.5rem', marginBottom: '1rem' }}>
            <h3 style={{ color: 'white', margin: 0 }}>
              {statusFilter === 'alerta' ? 'Mostrando Atletas en Alerta' : 'Directorio Completo'}
            </h3>
            {statusFilter !== 'all' && (
              <button 
                onClick={() => setStatusFilter('all')}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '0.4rem 1rem', borderRadius: '16px', cursor: 'pointer' }}
              >
                Limpiar filtro
              </button>
            )}
          </div>

          {filteredClients.length === 0 ? (
            <div className="glass-panel" style={{ padding: '5rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6', padding: '1.5rem', borderRadius: '50%' }}>
                <Users size={48} strokeWidth={1.5} />
              </div>
              <div>
                <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Aún no tienes atletas</h3>
                <p className="text-muted" style={{ maxWidth: '400px', margin: '0 auto' }}>
                  Invita a tu primer atleta usando el botón superior o carga la plantilla de prueba para ver cómo funciona el panel.
                </p>
              </div>
              <button 
                onClick={loadDemoSquad}
                style={{
                  background: 'linear-gradient(90deg, var(--health-cyan) 0%, var(--health-green) 100%)', color: 'black', border: 'none',
                  padding: '1rem 2rem', borderRadius: '24px', fontSize: '1rem', fontWeight: 'bold',
                  cursor: 'pointer', transition: 'all 0.2s', marginTop: '1rem',
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.2)'
                }}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Cargar Plantilla de Prueba
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
              
              {/* Función para renderizar el bloque de atleta (reutilizable) */}
              {(() => {
                const renderAthleteCard = (client, index, color) => (
                  <div key={client.id} className={`glass-panel glass-panel-interactive stagger-${(index % 4) + 1}`} 
                    onClick={() => onSelectAthlete && onSelectAthlete(client)}
                    style={{ 
                      display: 'flex', flexDirection: 'column', gap: '1rem', cursor: 'pointer',
                      borderTop: `3px solid ${color}`,
                      transition: 'all 0.2s ease-in-out'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = `0 8px 24px ${color}33`;
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {client.unreadMessagesCount > 0 && (
                      <div style={{ position: 'absolute', top: '-8px', right: '-8px', background: 'var(--health-cyan)', color: '#000', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 10 }}>
                        {client.unreadMessagesCount}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <img 
                        src={client.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=random`} 
                        alt={client.name} 
                        style={{ width: '60px', height: '60px', borderRadius: '50%', border: '2px solid var(--glass-border)' }}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color, boxShadow: `0 0 8px ${color}` }}></div>
                          <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{client.name}</h3>
                        </div>
                        <span style={{ 
                          display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', marginTop: '0.4rem',
                          background: client.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                          color: client.status === 'active' ? 'var(--health-green)' : '#f59e0b'
                        }}>
                          {client.status === 'active' ? 'Activo' : 'Pendiente'}
                        </span>
                      </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {(client.tags || ['General']).map(tag => (
                        <span key={tag} style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)', padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.65rem', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="text-muted text-sm">Readiness</span>
                        <span style={{ fontWeight: '700', color: color }}>{client.readinessScore || 0}%</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="text-muted text-sm">Estado</span>
                        <span className="text-sm">{client.recentStatus || 'Sin datos'}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid var(--glass-border)' }}>
                        <span className="text-muted text-xs">Última act.</span>
                        <span className="text-xs">{client.lastActivity || 'N/A'}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#8b5cf6', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        Borrador IA
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--strava-orange)', fontWeight: '600' }}>
                        Analizar ➔
                      </div>
                    </div>
                  </div>
                );

                const urgentClients = filteredClients.filter(c => c.recentStatus === 'Fatiga Alta' || c.readinessScore < 40);
                const moderateClients = filteredClients.filter(c => !urgentClients.includes(c) && (c.recentStatus === 'Recuperación' || (c.readinessScore >= 40 && c.readinessScore < 70)));
                const optimalClients = filteredClients.filter(c => !urgentClients.includes(c) && !moderateClients.includes(c));

                return (
                  <>
                    {/* TRIAGE SECTION 1: Atención Inmediata */}
                    {urgentClients.length > 0 && (
                      <div className="triage-section">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', paddingBottom: '0.75rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                          </span>
                          <h3 style={{ margin: 0, color: '#ef4444', fontSize: '1.4rem' }}>Atención Inmediata</h3>
                          <span style={{ marginLeft: 'auto', background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', padding: '0.2rem 0.8rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold' }}>{urgentClients.length} atletas</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                          {urgentClients.map((client, index) => renderAthleteCard(client, index, '#ef4444'))}
                        </div>
                      </div>
                    )}

                    {/* TRIAGE SECTION 2: Riesgo Moderado */}
                    {moderateClients.length > 0 && (
                      <div className="triage-section">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', borderBottom: '1px solid rgba(245, 158, 11, 0.2)', paddingBottom: '0.75rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          </span>
                          <h3 style={{ margin: 0, color: '#f59e0b', fontSize: '1.4rem' }}>Riesgo Moderado / Recuperación</h3>
                          <span style={{ marginLeft: 'auto', background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', padding: '0.2rem 0.8rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold' }}>{moderateClients.length} atletas</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                          {moderateClients.map((client, index) => renderAthleteCard(client, index, '#f59e0b'))}
                        </div>
                      </div>
                    )}

                    {/* TRIAGE SECTION 3: En Progreso / Óptimo */}
                    {optimalClients.length > 0 && (
                      <div className="triage-section">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', borderBottom: '1px solid rgba(16, 185, 129, 0.2)', paddingBottom: '0.75rem' }}>
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          </span>
                          <h3 style={{ margin: 0, color: '#10b981', fontSize: '1.4rem' }}>En Progreso / Óptimo</h3>
                          <span style={{ marginLeft: 'auto', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981', padding: '0.2rem 0.8rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold' }}>{optimalClients.length} atletas</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                          {optimalClients.map((client, index) => renderAthleteCard(client, index, '#10b981'))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
      </>
      )}

      {isAddModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          animation: 'fadeInTab 0.3s ease-out'
        }}>
          <div className="glass-panel" style={{
            width: '90%', maxWidth: '400px', padding: '2rem',
            display: 'flex', flexDirection: 'column', gap: '1.5rem',
            position: 'relative'
          }}>
            <button 
              onClick={() => {
                setIsAddModalOpen(false);
                setMagicLink('');
                setAddMessage({ text: '', type: '' });
                setAthleteEmail('');
                setAthleteName('');
              }}
              style={{
                position: 'absolute', top: '1rem', right: '1rem',
                background: 'transparent', border: 'none', color: 'var(--text-muted)',
                fontSize: '1.5rem', cursor: 'pointer'
              }}
            >
              &times;
            </button>
            <h2 className="text-xl">Invitar nuevo atleta</h2>
            
            {magicLink ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', animation: 'fadeInTab 0.3s ease-out' }}>
                <p className="text-sm" style={{ color: 'var(--health-green)' }}>¡Enlace de invitación generado con éxito!</p>
                <p className="text-muted text-sm">Copia este enlace y envíaselo a tu atleta. Cuando entre y configure su contraseña, quedará vinculado a ti automáticamente.</p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input 
                    type="text" 
                    readOnly 
                    value={magicLink}
                    style={{
                      flex: 1, padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--glass-border)',
                      background: 'rgba(0,0,0,0.3)', color: 'white', outline: 'none', fontSize: '0.8rem'
                    }}
                  />
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(magicLink);
                      alert('Enlace copiado al portapapeles');
                    }}
                    style={{
                      background: 'var(--health-cyan)', color: 'black', border: 'none', padding: '0 1rem', borderRadius: '8px',
                      fontWeight: 'bold', cursor: 'pointer'
                    }}
                  >
                    Copiar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-muted text-sm">Genera un "Magic Link" para invitar a tu deportista a la plataforma.</p>
                <form onSubmit={handleAddAthlete} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input 
                    type="text" 
                    value={athleteName}
                    onChange={(e) => setAthleteName(e.target.value)}
                    placeholder="Nombre del Atleta"
                    required
                    style={{
                      padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--glass-border)',
                      background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none'
                    }}
                  />
                  <input 
                    type="email" 
                    value={athleteEmail}
                    onChange={(e) => setAthleteEmail(e.target.value)}
                    placeholder="email@ejemplo.com"
                    required
                    style={{
                      padding: '0.8rem', borderRadius: '8px', border: '1px solid var(--glass-border)',
                      background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none'
                    }}
                  />
                  
                  {addMessage.text && (
                    <div style={{ 
                      color: addMessage.type === 'error' ? '#ef4444' : 'var(--health-green)',
                      fontSize: '0.875rem', background: addMessage.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                      padding: '0.5rem', borderRadius: '4px'
                    }}>
                      {addMessage.text}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    disabled={isAdding}
                    style={{
                      background: isAdding ? 'rgba(255,255,255,0.2)' : 'var(--strava-orange)',
                      color: 'white', border: 'none', padding: '0.8rem', borderRadius: '8px',
                      fontWeight: '600', cursor: isAdding ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isAdding ? 'Generando...' : 'Generar Enlace de Invitación'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
